import sum from 'lodash/sum';

import {abbreviateNumber, capitalize, commaizeNumber, ordinalizeNumber} from 'common/util';
import {pluralize} from 'common/util/string';
import {RetentionQuery} from 'common/report/data/retention/retention-query';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {formatPercent as formatPercentFromCommon} from 'common/util';

import {RetentionBookmarkParams} from './types';

const UNIT = {
  DAY: `day`,
  WEEK: `week`,
  MONTH: `month`,
};
const SMALLER_UNIT_MAP = {
  [UNIT.DAY]: `hour`,
  [UNIT.WEEK]: `day`,
  [UNIT.MONTH]: `week`,
};

export function getUnitSmaller(unit): string {
  return SMALLER_UNIT_MAP[unit];
}

export function getAddictionUnit(unit): string {
  // Simply becuase that is how addiction works
  return unit === UNIT.MONTH ? UNIT.DAY : getUnitSmaller(unit);
}

export function isAddiction(bookmarkParams: RetentionBookmarkParams) {
  return bookmarkParams.retention_type === `addiction`;
}

/**
 * Convert a decimal percentage to a formatted string percent to a certain precision
 * @example
 * formatPercent(0.2548, 1);
 * // 25.5%
 * formatPercent(12.342, 0);
 * // 1,234%
 **/

export function formatPercent(value: number, precision = 2): string {
  if (isNaN(value)) {
    value = 0;
  }

  return formatPercentFromCommon(value, precision);
}

/**
 * format a number to be ready to use in retention table
 * @param value number to be formatted
 * @returns {number}
 */
export function formatNumber(value: number): string {
  return value < 1000000 ? commaizeNumber(value) : abbreviateNumber(value);
}

/**
 * Convert to number order given a number
 * @param number
 */
export function numberToRetentionBucketNumber(number: number): string {
  return number === 0 ? `<1` : ordinalizeNumber(number);
}

export function getRetentionBucketName({
  bucketIndex,
  bookmarkParams,
}: {
  bucketIndex: number;
  bookmarkParams: RetentionBookmarkParams;
}) {
  return isAddiction(bookmarkParams) || !bookmarkParams.bucket_sizes?.length
    ? getRetentionNonCustomBucketName({bucketIndex, bookmarkParams})
    : getRetentionCustomBucketName({bucketIndex, bookmarkParams});
}

export function getRetentionNonCustomBucketName({
  bucketIndex,
  bookmarkParams,
}: {
  bucketIndex: number;
  bookmarkParams: RetentionBookmarkParams;
}) {
  return isAddiction(bookmarkParams)
    ? `${bucketIndex + 2} ${capitalize(getAddictionUnit(bookmarkParams.date_unit))}`
    : bucketIndex > 0
    ? `${capitalize(bookmarkParams.retention_unit)} ${bucketIndex}`
    : `< 1 ${capitalize(bookmarkParams.retention_unit)}`;
}

export function getRetentionCustomBucketName({
  bucketIndex,
  bookmarkParams,
}: {
  bucketIndex: number;
  bookmarkParams: RetentionBookmarkParams;
}) {
  const capitalizedRetentionUnit = capitalize(bookmarkParams.retention_unit);
  const start = sum(bookmarkParams.bucket_sizes.slice(0, bucketIndex));
  const end = start + bookmarkParams.bucket_sizes[bucketIndex] - 1;

  return `${
    bucketIndex === 0
      ? `<${end + 1} ${pluralize(capitalizedRetentionUnit, end + 1)}`
      : `${
          bookmarkParams.bucket_sizes[bucketIndex] === 1
            ? `${capitalizedRetentionUnit} ${start}`
            : `${pluralize(capitalizedRetentionUnit, true)} ${start}-${end}`
        }`
  }`;
}

export function getRetentionBucketStartAndEnd({
  bucketIndex,
  bookmarkParams,
}: {
  bucketIndex: number;
  bookmarkParams: RetentionBookmarkParams;
}) {
  const start = sum(bookmarkParams.bucket_sizes.slice(0, bucketIndex));
  const end = start + bookmarkParams.bucket_sizes[bucketIndex] - 1;
  return {start, end};
}

export function getOrdinalRetentionBucketName(index: number, retentionUnit: TimeUnit) {
  return `${numberToRetentionBucketNumber(index)} ${retentionUnit}`;
}

export function getFrequencyTooltipSubtitle({
  cumulative,
  dateUnit,
  bucketIndex,
}: {
  cumulative: boolean;
  dateUnit: TimeUnit;
  bucketIndex: number;
}) {
  const count = bucketIndex + 2;
  return `${cumulative ? `Did [A] at least` : `In minimum`} ${count} ${pluralize(
    getAddictionUnit(dateUnit),
    count,
  )} in a ${dateUnit}`;
}

export function frequencyLineChartDescription(query: RetentionQuery) {
  const {dateUnit} = query;
  const dataGroupName = `${query.getDataGroupName()}s`;

  if (!query.cumulative) {
    return `The number of ${getAddictionUnit(dateUnit)} in a ${dateUnit} ${dataGroupName} did A`;
  } else {
    return `${capitalize(dataGroupName)} did A at least X ${dateUnit === `day` ? `hours` : `days`} in a ${dateUnit}`;
  }
}

export function retentionLineChartDescription(query: RetentionQuery) {
  const {retentionUnit} = query;
  const dataGroupName = `${query.getDataGroupName()}s`;

  if (query.unbounded) {
    return `X and more ${retentionUnit}s later your ${dataGroupName} came back and did B.`;
  } else {
    return `The number of exact ${retentionUnit}s later your ${dataGroupName} came back and did B.`;
  }
}
